/**
 * Shared Highcharts theme configuration for dark mode support.
 *
 * Provides consistent theming across all Highcharts instances in the application
 * with automatic dark mode detection and live theme switching support.
 */

import type * as HighchartsModule from "highcharts";

/**
 * Extended Highcharts interface for global usage.
 * Includes methods available at runtime but not in the base type definitions.
 */
export interface HighchartsStatic extends Omit<typeof HighchartsModule, "chart"> {
  chart(options: HighchartsModule.Options): HighchartsModule.Chart;
  chart(renderTo: string | HTMLElement, options: HighchartsModule.Options): HighchartsModule.Chart;
  stockChart(options: HighchartsModule.Options): HighchartsModule.Chart;
  stockChart(
    renderTo: string | HTMLElement,
    options: HighchartsModule.Options,
  ): HighchartsModule.Chart;
  setOptions(options: HighchartsModule.Options): HighchartsModule.Options;
  getOptions(): HighchartsModule.Options;
}

/**
 * Dark theme options for Highcharts.
 * Based on the Dark Unica theme with customizations for SAM.
 */
export const DARK_THEME_OPTIONS: HighchartsModule.Options = {
  // Use local timezone for display to avoid confusing UTC date shifts
  time: { useUTC: false },
  colors: [
    "#2b908f",
    "#90ee7e",
    "#f45b5b",
    "#7798BF",
    "#aaeeee",
    "#ff0066",
    "#eeaaee",
    "#55BF3B",
    "#DF5353",
    "#7798BF",
    "#aaeeee",
  ],
  chart: {
    backgroundColor: "transparent",
    style: {
      fontFamily: "'Unica One', sans-serif",
    },
    plotBorderColor: "#606063",
  },
  title: {
    style: {
      color: "#E0E0E3",
      textTransform: "uppercase",
      fontSize: "20px",
    },
  },
  subtitle: {
    style: {
      color: "#E0E0E3",
      textTransform: "uppercase",
    },
  },
  xAxis: {
    gridLineColor: "#707073",
    labels: {
      style: {
        color: "#E0E0E3",
      },
    },
    lineColor: "#707073",
    minorGridLineColor: "#505053",
    tickColor: "#707073",
    title: {
      style: {
        color: "#A0A0A3",
      },
    },
  },
  yAxis: {
    gridLineColor: "#707073",
    labels: {
      style: {
        color: "#E0E0E3",
      },
    },
    lineColor: "#707073",
    minorGridLineColor: "#505053",
    tickColor: "#707073",
    tickWidth: 1,
    title: {
      style: {
        color: "#A0A0A3",
      },
    },
  },
  tooltip: {
    backgroundColor: "rgba(0, 0, 0, 0.85)",
    style: {
      color: "#F0F0F0",
    },
  },
  plotOptions: {
    series: {
      dataLabels: {
        color: "#F0F0F3",
        style: {
          fontSize: "13px",
        },
      },
      marker: {
        lineColor: "#333",
      },
    },
    boxplot: {
      fillColor: "#505053",
    },
    candlestick: {
      lineColor: "white",
    },
    errorbar: {
      color: "white",
    },
  },
  legend: {
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    itemStyle: {
      color: "#E0E0E3",
    },
    itemHoverStyle: {
      color: "#FFF",
    },
    itemHiddenStyle: {
      color: "#606063",
    },
    title: {
      style: {
        color: "#C0C0C0",
      },
    },
  },
  credits: {
    style: {
      color: "#666",
    },
  },
  drilldown: {
    activeAxisLabelStyle: {
      color: "#F0F0F3",
    },
    activeDataLabelStyle: {
      color: "#F0F0F3",
    },
  },
  navigation: {
    buttonOptions: {
      symbolStroke: "#DDDDDD",
      theme: {
        fill: "#505053",
      } as unknown as HighchartsModule.NavigationButtonThemeOptions,
    },
    menuStyle: {
      background: "#505053",
      border: "1px solid #666",
      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.5)",
    },
    menuItemStyle: {
      color: "#E0E0E3",
      fontSize: "13px",
      background: "#505053",
      padding: "0.5em 1em",
    },
    menuItemHoverStyle: {
      background: "#707073",
      color: "#FFF",
    },
  },
  // Stock chart specific options
  rangeSelector: {
    buttonTheme: {
      fill: "#505053",
      stroke: "#000000",
      style: {
        color: "#CCC",
      },
      states: {
        hover: {
          fill: "#707073",
          stroke: "#000000",
          style: {
            color: "white",
          },
        },
        select: {
          fill: "#000003",
          stroke: "#000000",
          style: {
            color: "white",
          },
        },
      },
    },
    inputBoxBorderColor: "#505053",
    inputStyle: {
      backgroundColor: "#333",
      color: "silver",
    },
    labelStyle: {
      color: "silver",
    },
  },
  navigator: {
    handles: {
      backgroundColor: "#666",
      borderColor: "#AAA",
    },
    outlineColor: "#CCC",
    maskFill: "rgba(255,255,255,0.1)",
    series: {
      color: "#7798BF",
      lineColor: "#A6C7ED",
    },
    xAxis: {
      gridLineColor: "#505053",
    },
  },
  scrollbar: {
    barBackgroundColor: "#808083",
    barBorderColor: "#808083",
    buttonArrowColor: "#CCC",
    buttonBackgroundColor: "#606063",
    buttonBorderColor: "#606063",
    rifleColor: "#FFF",
    trackBackgroundColor: "#404043",
    trackBorderColor: "#404043",
  },
};

/**
 * Light theme options for Highcharts.
 * Explicit defaults to override dark theme when switching modes.
 * Since Highcharts.setOptions() merges rather than replaces,
 * we must explicitly set all properties that dark theme overrides.
 */
export const LIGHT_THEME_OPTIONS: HighchartsModule.Options = {
  time: { useUTC: false },
  colors: [
    "#2caffe",
    "#544fc5",
    "#00e272",
    "#fe6a35",
    "#6b8abc",
    "#d568fb",
    "#2ee0ca",
    "#fa4b42",
    "#feb56a",
    "#91e8e1",
  ],
  chart: {
    backgroundColor: "#ffffff",
    style: {
      fontFamily: '"Lucida Grande", "Lucida Sans Unicode", Verdana, Arial, Helvetica, sans-serif',
    },
    plotBorderColor: "#cccccc",
  },
  title: {
    style: {
      color: "#333333",
      textTransform: "none",
      fontSize: "18px",
    },
  },
  subtitle: {
    style: {
      color: "#666666",
      textTransform: "none",
    },
  },
  xAxis: {
    gridLineColor: "#e6e6e6",
    labels: {
      style: {
        color: "#666666",
      },
    },
    lineColor: "#ccd6eb",
    minorGridLineColor: "#f2f2f2",
    tickColor: "#ccd6eb",
    title: {
      style: {
        color: "#666666",
      },
    },
  },
  yAxis: {
    gridLineColor: "#e6e6e6",
    labels: {
      style: {
        color: "#666666",
      },
    },
    lineColor: "#ccd6eb",
    minorGridLineColor: "#f2f2f2",
    tickColor: "#ccd6eb",
    tickWidth: 0,
    title: {
      style: {
        color: "#666666",
      },
    },
  },
  tooltip: {
    backgroundColor: "rgba(247, 247, 247, 0.85)",
    style: {
      color: "#333333",
    },
  },
  plotOptions: {
    series: {
      dataLabels: {
        color: "#333333",
        style: {
          fontSize: "11px",
        },
      },
      marker: {
        lineColor: "#ffffff",
      },
    },
    boxplot: {
      fillColor: "#ffffff",
    },
    candlestick: {
      lineColor: "#000000",
    },
    errorbar: {
      color: "#000000",
    },
  },
  legend: {
    backgroundColor: "transparent",
    itemStyle: {
      color: "#333333",
    },
    itemHoverStyle: {
      color: "#000000",
    },
    itemHiddenStyle: {
      color: "#cccccc",
    },
    title: {
      style: {
        color: "#333333",
      },
    },
  },
  credits: {
    style: {
      color: "#999999",
    },
  },
  drilldown: {
    activeAxisLabelStyle: {
      color: "#333333",
    },
    activeDataLabelStyle: {
      color: "#333333",
    },
  },
  navigation: {
    buttonOptions: {
      symbolStroke: "#666666",
      theme: {
        fill: "#ffffff",
      } as unknown as HighchartsModule.NavigationButtonThemeOptions,
    },
    menuStyle: {
      background: "#ffffff",
      border: "1px solid #999999",
      boxShadow: "none",
    },
    menuItemStyle: {
      color: "#333333",
      fontSize: "11px",
      background: "transparent",
      padding: "0.5em 1em",
    },
    menuItemHoverStyle: {
      background: "#335cad",
      color: "#ffffff",
    },
  },
  // Stock chart specific options
  rangeSelector: {
    buttonTheme: {
      fill: "#f7f7f7",
      stroke: "#cccccc",
      style: {
        color: "#333333",
      },
      states: {
        hover: {
          fill: "#e6e6e6",
          stroke: "#cccccc",
          style: {
            color: "#000000",
          },
        },
        select: {
          fill: "#e6ebf5",
          stroke: "#cccccc",
          style: {
            color: "#000000",
          },
        },
      },
    },
    inputBoxBorderColor: "#cccccc",
    inputStyle: {
      backgroundColor: "#ffffff",
      color: "#333333",
    },
    labelStyle: {
      color: "#666666",
    },
  },
  navigator: {
    handles: {
      backgroundColor: "#f2f2f2",
      borderColor: "#999999",
    },
    outlineColor: "#cccccc",
    maskFill: "rgba(102,133,194,0.3)",
    series: {
      color: "#335cad",
      lineColor: "#335cad",
    },
    xAxis: {
      gridLineColor: "#e6e6e6",
    },
  },
  scrollbar: {
    barBackgroundColor: "#cccccc",
    barBorderColor: "#cccccc",
    buttonArrowColor: "#333333",
    buttonBackgroundColor: "#e6e6e6",
    buttonBorderColor: "#cccccc",
    rifleColor: "#333333",
    trackBackgroundColor: "#f2f2f2",
    trackBorderColor: "#f2f2f2",
  },
};

/**
 * Checks if dark mode is currently active.
 *
 * @returns True if dark mode is enabled.
 */
export function isDarkMode(): boolean {
  return (
    document.documentElement.classList.contains("dark") || document.body.classList.contains("dark")
  );
}

/**
 * Initializes Highcharts theme based on current dark mode state.
 *
 * @param Highcharts - The Highcharts instance to configure.
 */
export function initializeHighchartsTheme(Highcharts: HighchartsStatic): void {
  if (typeof Highcharts === "undefined") {
    return;
  }

  if (isDarkMode()) {
    Highcharts.setOptions(DARK_THEME_OPTIONS);
  } else {
    // Reset to default Highcharts theme for light mode
    Highcharts.setOptions({
      ...Highcharts.getOptions(),
      ...LIGHT_THEME_OPTIONS,
    });
  }
}

/**
 * Sets up a MutationObserver to watch for dark mode changes.
 *
 * Only triggers callback when dark mode state actually changes,
 * not on every class modification (e.g., Alpine.js x-cloak removal).
 *
 * @param onThemeChange - Callback to execute when theme changes.
 * @returns The MutationObserver instance (call .disconnect() to clean up).
 */
export function setupThemeObserver(onThemeChange: () => void): MutationObserver {
  // Track current dark mode state to avoid spurious triggers
  let wasDarkMode = isDarkMode();

  const observer = new MutationObserver((mutations: MutationRecord[]) => {
    for (const mutation of mutations) {
      if (mutation.type === "attributes" && mutation.attributeName === "class") {
        const isNowDarkMode = isDarkMode();
        // Only trigger if dark mode state actually changed
        if (isNowDarkMode !== wasDarkMode) {
          wasDarkMode = isNowDarkMode;
          onThemeChange();
        }
        break;
      }
    }
  });

  // Observe changes to dark mode class on html element
  observer.observe(document.documentElement, { attributes: true });

  // Also observe body if it exists (some apps toggle dark class there)
  if (document.body) {
    observer.observe(document.body, { attributes: true });
  }

  return observer;
}
